import type { QueryClient } from '@tanstack/react-query';
import { queryUtils } from '@vizit/api';

const BENCHMARK_QUERY_NAMES = new Set([
  'GetBenchmark',
  'ListBenchmarkImages',
  'ListBenchmarkScorings',
  'ListBenchmarkCollections',
]);

/**
 * Refetches everything the page reads about one benchmark: the benchmark itself, every list of its images (whatever
 * their sort or category), its scorings and the collections scored against it, and with them the workspace's
 * benchmark lists and collection pickers, which count its images. For when the server changed the benchmark behind
 * the page's back: an upload created a record or landed in storage, images were removed, or a scoring finished.
 */
function refreshBenchmark(queryClient: QueryClient, workspaceId: string, benchmarkId: string) {
  return Promise.all([
    queryClient.invalidateQueries({
      predicate: ({ queryKey: [name, variables] }) => {
        const scope = variables as { workspace_id?: string; benchmark_id?: string } | undefined;
        return (
          BENCHMARK_QUERY_NAMES.has(name as string) &&
          scope?.workspace_id === workspaceId &&
          scope?.benchmark_id === benchmarkId
        );
      },
    }),
    queryUtils.invalidateAllBenchmarkLists(queryClient, workspaceId),
  ]);
}

/**
 * Marks stale what says which collections are scored against a benchmark: the benchmark lists and the benchmark
 * itself (their collection count) and its collections listing. For when a scoring of a collection against a custom
 * benchmark is requested or lands, or a collection is deleted. The delete confirm and the upload notice count from
 * these, and a cached 0 would let both skip their acknowledgement. Without a benchmark, every one of the workspace is.
 */
function refreshBenchmarkCollections(queryClient: QueryClient, workspaceId: string, benchmarkId?: string | null) {
  return queryClient.invalidateQueries({
    predicate: ({ queryKey: [name, variables] }) => {
      const scope = variables as { workspace_id?: string; benchmark_id?: string } | undefined;
      if (scope?.workspace_id !== workspaceId) {
        return false;
      }
      if (name === 'SearchBenchmarks') {
        return true;
      }
      return (
        (name === 'GetBenchmark' || name === 'ListBenchmarkCollections') &&
        (!benchmarkId || scope.benchmark_id === benchmarkId)
      );
    },
  });
}

/**
 * Wraps a refresh so that a burst of requests for it costs a few reads instead of one each. The first request runs
 * right away; any made while a run is in flight or within `intervalMs` of its start share one more run, once both
 * have passed. Runs never overlap: an invalidation restarts a read still in flight, so refreshing on every request
 * kept restarting the lists, and they only caught up once the requests stopped. A failed run is left to the queries'
 * own error state. `onIdle` is called once a run ends with nothing more requested: every request has been read.
 */
function throttleRefresh(
  refresh: () => Promise<unknown>,
  intervalMs: number,
  { onIdle }: { onIdle?: () => void } = {},
) {
  let lastStartedAt = Number.NEGATIVE_INFINITY;
  let isRunning = false;
  let isRequested = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const schedule = () => {
    if (isRunning || timer) {
      return;
    }
    const wait = lastStartedAt + intervalMs - Date.now();
    if (wait > 0) {
      timer = setTimeout(run, wait);
      return;
    }
    run();
  };

  function run() {
    timer = undefined;
    isRequested = false;
    isRunning = true;
    lastStartedAt = Date.now();
    refresh()
      .catch(() => undefined)
      .finally(() => {
        isRunning = false;
        if (isRequested) {
          schedule();
        } else {
          onIdle?.();
        }
      });
  }

  return () => {
    isRequested = true;
    schedule();
  };
}

export { refreshBenchmark, refreshBenchmarkCollections, throttleRefresh };
